import React, { useEffect, useRef, useState } from "react";
import SEO from "../components/seo";
import { getFaqPageJsonLd } from "../utils/jsonld";
import { Container } from "reactstrap";
import TitleBannerEnquiry from "../components/titleBanner/TitleBannerEnquiry";
import "../styles/faq.css";
const faqs = [
    {
        question: "What will I learn?",
        bullets: [
            "Each course page has a course overview and a set of learning outcomes.",
            "Learners are strongly advised to understand the course overview and learning outcomes to determine the course's relevance to their employment.",
            "If you require more clarification, learners may reach us at training@aia.edu.sg or submit an enquiry form.",
        ],
    },
    {
        question: "What are the entry requirements?",
        bullets: [
            "Each course page has a set of entry requirements.",
            "Learners who are applying for funding from SkillsFuture must meet all entry requirements of the course they are applying for.",
            "Learners who are not applying for funding shall minimally meet the work experience or any equivalent course requirements to help you understand the topics in class.",
        ],
    },
    {
        question: "What is the attendance requirement?",
        answer:
            "We strongly advise that learners meet at least 75% of the attendance requirements to keep up with the learnings in class and be better positioned to pass the assessment.",
    },
    { question: "What are the course completion requirements?", answer: "Complete and pass the assessment requirements." },
    {
        question: "What if I do not pass the assessment?",
        bullets: [
            "You may choose to re-take the assessment. Each re-assessment is subject to a fee of $100 (before GST).",
            "If you do not meet the course completion requirements, Course Fee Funding from SkillsFuture Singapore is no longer applicable. Learners or employers (where applicable) will be responsible for paying the balance of the full course fee.",
        ],
    },
    {
        question: "What certificate will I get?",
        bullets: [
            "For WSQ-accredited courses, learners will be conferred a WSQ certificate for meeting all course completion requirements.",
            "For non-WSQ-accredited courses, learners who meet all course completion requirements will be accorded a Certificate of Competence from Ace Industrial Academy.",
        ],
    },
    {
        question: "What are the fundings available?",
        bullets: [
            '<a href="https://www.aia.edu.sg/funding">Course fee fundings</a> are available to <a href="https://www.aia.edu.sg/funding">self-sponsored and/or company-sponsored participants</a>.',
            "Self-sponsored participants can use their SkillsFuture Credit to offset the nett course fees.",
            "Employers of company-sponsored participants are eligible for absentee payroll funding, and can use the SkillsFuture Enterprise Credit to offset nett course fees.",
            'Singapore Citizen students or students with Singapore Citizen siblings may be eligible to use <a href="https://www.aia.edu.sg/PSEA">Post-Secondary Education Account (PSEA)</a> funds to offset course fees.',
            'If you need clarification on funding matters, please reach us at <a href="mailto:training@aia.edu.sg">training@aia.edu.sg</a> or submit an enquiry form.',
        ],
    },
    {
        question: "Why is AIA collecting my NRIC number and birth date?",
        bullets: [
            "Your full NRIC number is required for us to check your funding eligibility.",
            "Only AIA’s staff processing your application will request for your NRIC.",
            'Our staff are listed here: <a href="https://www.aia.edu.sg/theteam/">BIM & CAD Training Center | Ace Industrial Academy | BIM & CAD Training Center</a>.',
            'You may also refer to our policy on <a href="https://www.aia.edu.sg/schedule/">personal data protection</a>.',
        ],
    },
    {
        question: "What is the process for funding application?",
        bullets: [
            "For self-sponsored participants (Singaporean/PR), funding will be automatically calculated during the billing process. Ensure the NRIC provided is correct to confirm eligibility.",
            "For company-sponsored participants (Singaporean/PR), provide your company UEN to calculate eligible funding. SMEs that meet the criteria shall receive additional support; funding is calculated automatically during billing.",
        ],
    },
];

// smooth height without libs
function useAutoHeight(isOpen) {
    const ref = useRef(null);
    const [maxH, setMaxH] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        setMaxH(isOpen ? el.scrollHeight : 0);
    }, [isOpen]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            if (isOpen) setMaxH(el.scrollHeight);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [isOpen]);

    return { ref, style: { maxHeight: maxH } };
}

export default function FAQ() {
    const [openMap, setOpenMap] = useState(() => faqs.map(() => false));

    const expandAll = () => setOpenMap(faqs.map(() => true));
    const collapseAll = () => setOpenMap(faqs.map(() => false));
    const toggleIndex = (i) => setOpenMap((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

    const openCount = openMap.filter(Boolean).length;
    const allOpen = openCount === faqs.length;
    const noneOpen = openCount === 0;

    // JSON-LD needs a plain text answer
    const faqsForLd = faqs.map((f) => ({
        question: f.question,
        answer: f.bullets?.length ? f.bullets.join(" ") : f.answer,
    }));
    const jsonLd = getFaqPageJsonLd(faqsForLd);

    return (
        <>
            <SEO
                title="BIM & CAD Training Center | Ace Industrial Academy"
                description="Ace Industrial Academy is a leading training center in Singapore for IT courses, BIM courses, 3D CAD courses, 4D courses, software courses, building information modeling courses and more."
                jsonLd={jsonLd}
            />

            <TitleBannerEnquiry
                title="Frequently Asked Questions"
                description="Everything you need to know before you enrol — funding, attendance, certificates, and more."
                withOnlyDescription
                hasPlainBanner
            />

            <Container className="pt-4">
                <div className="faq-grid">
                    {/* Left column */}
                    <div>
                        {/* Segmented Expand/Collapse with count */}
                        <div className="faq-toolbar">
                            <button
                                type="button"
                                className={`toggle-all ${allOpen ? "on" : ""}`}
                                onClick={allOpen ? collapseAll : expandAll}
                                aria-pressed={allOpen}
                            >
                                <span className="ico" aria-hidden>{allOpen ? "−" : "+"}</span>
                                <span>{allOpen ? "Collapse all" : "Expand all"}</span>
                            </button>

                            <span className="seg-count" aria-live="polite">
                                {openCount}/{faqs.length} open
                            </span>
                        </div>

                        <ul className="faq-list">
                            {faqs.map((item, i) => {
                                const isOpen = openMap[i];
                                const { ref, style } = useAutoHeight(isOpen);
                                return (
                                    <li key={i}>
                                        <article className={`faq-card ${i % 2 === 1 ? "alt" : ""} ${isOpen ? "open" : ""}`} id={`q-${i}`}>
                                            <button className="faq-question" aria-expanded={isOpen} onClick={() => toggleIndex(i)}>
                                                <span>{item.question}</span>
                                                <span className="chev" aria-hidden />
                                            </button>

                                            <div className="faq-answer" style={style} aria-hidden={!isOpen}>
                                                <div className="inner" ref={ref}>
                                                    <ul className="ansList">
                                                        {(item.bullets?.length ? item.bullets : [item.answer]).map((b, idx) => (
                                                            <li key={idx} dangerouslySetInnerHTML={{ __html: linkify(b) }} />
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </article>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Right column */}
                    <aside className="help-card">
                        <h3>Have a question that’s not listed?</h3>
                        <p>
                            Reach us at <a href="mailto:training@aia.edu.sg">training@aia.edu.sg</a> or submit an enquiry form.
                        </p>
                    </aside>
                </div>
            </Container>
        </>
    );
}

function linkify(str) {
    if (!str) return "";
    if (/<a\s/i.test(str)) return str;

    const combined = /(https?:\/\/[^\s<)]+)|(\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b)/gi;
    return str.replace(combined, (m) =>
        m.toLowerCase().startsWith("http")
            ? `<a href="${m}" target="_blank" rel="noopener">${m}</a>`
            : `<a href="mailto:${m}">${m}</a>`
    );
}